import {
  ActionIcon,
  Alert,
  Anchor,
  AnchorProps,
  Accordion,
  Badge,
  Box,
  Drawer,
  Group,
  Indicator,
  Loader,
  ScrollArea,
  Text,
  Tooltip,
  TypographyStylesProvider,
  Button,
  Flex,
  Paper
} from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLatestUpdates, useMarkUpdatesAsSeen } from '../../hooks/useUpdates';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const formatDate = (value?: string | Date | null): string => {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

const getUnreadLabel = (count?: number): string | undefined => {
  if (!count || count <= 0) return undefined;
  if (count > 9) return '9+';
  return String(count);
};

const getPreview = (update: any) => {
  if (update?.summary?.trim()) return update.summary.trim();
  const raw = String(update?.body || '');
  const cleaned = raw.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.slice(0, 160) + (cleaned.length > 160 ? '…' : '');
};

const getTypeLabel = (type?: string) => {
  switch (type) {
    case 'feature':
      return 'Nuevo';
    case 'fix':
      return 'Fix';
    case 'improvement':
      return 'Mejora';
    case 'announcement':
      return 'Aviso';
    default:
      return type || '';
  }
};

export const UpdatesButton = () => {
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation('Updates');
  const { data, isLoading, isError } = useLatestUpdates(10);
  const markSeen = useMarkUpdatesAsSeen();
  const hasMarkedThisOpen = useRef(false);

  const unreadLabel = useMemo(() => getUnreadLabel(data?.unreadCount), [data?.unreadCount]);
  const updates = data?.updates ?? [];

  useEffect(() => {
    if (!opened) {
      hasMarkedThisOpen.current = false;
      return;
    }
    if (opened && data?.unreadCount && data.unreadCount > 0 && !hasMarkedThisOpen.current) {
      hasMarkedThisOpen.current = true;
      markSeen.mutate();
    }
  }, [opened, data?.unreadCount, markSeen]);

  return (
    <>
      <Tooltip label={t('tooltip')}>
        <Indicator
          position="top-end"
          offset={5}
          disabled={!unreadLabel}
          label={unreadLabel}
          size="sm"
          color="red">
          <ActionIcon
            radius="none"
            variant="default"
            aria-label="ver-novedades"
            size="lg"
            onClick={() => setOpened(true)}>
            <IconSparkles style={{ width: '70%', height: '70%' }} stroke={1.5} />
          </ActionIcon>
        </Indicator>
      </Tooltip>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        title={t('title')}
        size="lg"
        padding="md">
        <ScrollArea h="100%" type="always">
          {isLoading ? (
            <Flex justify="center" py="md">
              <Loader />
            </Flex>
          ) : isError ? (
            <Alert color="red" title="Error">
              {t('loadError')}
            </Alert>
          ) : updates.length === 0 ? (
            <Text c="dimmed">{t('empty')}</Text>
          ) : (
            <Accordion variant="separated" radius="md">
              {updates.map((update, idx) => (
                <Accordion.Item key={update._id || idx} value={String(update._id || idx)}>
                  <Paper withBorder radius="md" p="md">
                    <Accordion.Control>
                      <Box style={{ flex: 1 }}>
                        <Group gap="xs" mb={2} wrap="nowrap">
                          <Text fw={600} size="sm" lineClamp={2} style={{ flex: 1 }}>
                            {update.title}
                          </Text>

                          {update.pinned ? (
                            <Badge size="xs" variant="light">
                              Fijado
                            </Badge>
                          ) : null}

                          {update.type ? (
                            <Badge size="xs" variant="light">
                              {getTypeLabel(update.type)}
                            </Badge>
                          ) : null}
                        </Group>

                        <Text size="xs" c="dimmed" mb={6}>
                          {formatDate(update.publishedAt || update.createdDate)}
                        </Text>

                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {getPreview(update)}
                        </Text>
                      </Box>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <Box mt="sm">
                        {update.bodyFormat === 'html' ? (
                          <TypographyStylesProvider>
                            <div
                              style={{ fontSize: '0.9rem' }}
                              dangerouslySetInnerHTML={{ __html: update.body || '' }}
                            />
                          </TypographyStylesProvider>
                        ) : (
                          <TypographyStylesProvider>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                a: ({ node, ...props }) => (
                                  <Anchor
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    {...(props as AnchorProps)}
                                  />
                                )
                              }}>
                              {update.body || ''}
                            </ReactMarkdown>
                          </TypographyStylesProvider>
                        )}

                        {Array.isArray(update.links) && update.links.length > 0 ? (
                          <Group mt="sm" gap="xs">
                            {update.links.map((link: any, linkIdx: number) => (
                              <Button
                                key={`${link.url}-${linkIdx}`}
                                component="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="light"
                                size="xs">
                                {link.label || t('viewMore')}
                              </Button>
                            ))}
                          </Group>
                        ) : null}
                      </Box>
                    </Accordion.Panel>
                  </Paper>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </ScrollArea>
      </Drawer>
    </>
  );
};
